import { AnyAction, Dispatch } from '@reduxjs/toolkit';
import { isServerContext } from '@squareup/dex-utils-environment';
import React, { Suspense, ReactNode, useRef } from 'react';

interface SuspendUntilProps {
  children: ReactNode;
  untilDispatch: <AppDispatch extends Dispatch<AnyAction>>() => AppDispatch;
  isLoaded: () => boolean | undefined;
}

/**
 * Wraps the given promise providing a suspension api around it.
 * Upon read will throw until the given promise is resolved.
 */
function wrapAsSuspensePromise(promise: Promise<unknown>) {
  let status = 'pending';
  let result: unknown;
  const suspender = promise.then(
    (r) => {
      status = 'success';
      result = r;
    },
    (error) => {
      status = 'error';
      result = error;
    }
  );
  return {
    read(): unknown {
      if (status === 'pending') {
        throw suspender;
      } else if (status === 'error') {
        throw result;
      } else {
        return result;
      }
    },
  };
}

/**
 * If not server side kicks of a sync fetch throwing until it resolves or the is in cache returns true.
 */
const AwaitUntil = ({
  children,
  untilDispatch,
  isLoaded,
}: SuspendUntilProps) => {
  const hasLoadedRef = useRef(isLoaded());

  if (!hasLoadedRef.current && isLoaded()) {
    hasLoadedRef.current = true;
  }

  if (!isServerContext() && !hasLoadedRef.current) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { read } = wrapAsSuspensePromise(untilDispatch<any>());

    read();
  }

  return <>{children}</>;
};

/**
 * This component will suspend client hydration until the given 'untilAction' is fulfilled or isInCache is true.
 * This allows for a render while you fetch flow within react.
 * Other use cases of this component may be SSR scenarios where you wish to do your SSR render followed
 * by a client side fetch all while keeping the DOM matching in all cases.
 */
const SuspendUntil = ({
  children,
  untilDispatch,
  isLoaded,
}: SuspendUntilProps) => {
  return (
    <Suspense>
      <AwaitUntil isLoaded={isLoaded} untilDispatch={untilDispatch}>
        {children}
      </AwaitUntil>
    </Suspense>
  );
};

export { SuspendUntil };
